import { SearchField } from '@servicetitan/anvil2';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import {
    ChangeEvent,
    FC,
    FocusEvent,
    KeyboardEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useDoSearch, useSearchStore } from '../../shared';
import * as Styles from './search-bar-case.module.less';

interface SearchBarCaseProps {
    className?: string;
    hideOnBlur?: boolean;
    size?: 'small' | 'medium' | 'large';
    placeholder?: string;
    dataCy?: string;
    dataPendo?: string;
}

const SEARCH_DEBOUNCE_MS = 1000;

export const SearchBarCase: FC<SearchBarCaseProps> = observer(
    ({
        className,
        dataCy = 'ish-search-input',
        dataPendo = 'ish-search-input',
        hideOnBlur = false,
        placeholder = 'Search support case',
        size = 'large',
    }) => {
        const searchFieldRef = useRef<HTMLInputElement | null>(null);
        const [query, setQuery] = useState<string>('');
        const searchStore = useSearchStore();
        const doSearch = useDoSearch();
        const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        const location = useLocation();
        const prevLocationRef = useRef(location.pathname);

        useEffect(() => {
            searchStore.searchInputEl = searchFieldRef.current ?? undefined;
            searchStore.setHideOnBlur(hideOnBlur);
            // If hideOnBlur is false (search page), keep results visible
            if (!hideOnBlur) {
                searchStore.setIsFocused(true);
            }
            const id = setTimeout(() => {
                searchFieldRef.current?.focus();
            }, 0);
            return () => clearTimeout(id);
        }, [searchStore, hideOnBlur]);

        // Clear search when navigating to a different page
        useEffect(() => {
            if (prevLocationRef.current !== location.pathname) {
                prevLocationRef.current = location.pathname;
                setQuery('');
                searchStore.setQuery('');
                searchStore.clearCases();
            }
        }, [location.pathname, searchStore]);

        const resetTimeout = useCallback(() => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        }, []);

        const triggerSearch = useCallback(
            async (query: string) => {
                resetTimeout();
                await doSearch(query);
            },
            [doSearch, resetTimeout]
        );

        const handleChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                const newQuery = event.target.value;
                setQuery(() => newQuery);
                resetTimeout();
                if (newQuery.length < 3) {
                    // Immediate search for queries shorter than 3 characters
                    triggerSearch(newQuery).then();
                    return;
                }
                // Debounce search
                timeoutRef.current = setTimeout(
                    async () => triggerSearch(newQuery),
                    SEARCH_DEBOUNCE_MS
                );
            },
            [resetTimeout, triggerSearch]
        );

        const handleKeydown = useCallback(
            async (event: KeyboardEvent<HTMLInputElement>) => {
                // Enter triggers immediate search
                if (event.key === 'Enter') {
                    await triggerSearch(query);
                }

                // Arrow down switches focus to the first search result
                if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    searchStore.resetFocusedResult();
                    searchStore.focusNextResult();
                }
            },
            [query, searchStore, triggerSearch]
        );

        const handleClear = useCallback(async () => {
            setQuery('');
            await triggerSearch('');
        }, [triggerSearch]);

        const handleFocus = useCallback(() => {
            // Reset focused result when focusing the search input
            searchStore.resetFocusedResult();
            searchStore.setIsFocused(true);
        }, [searchStore]);

        const handleBlur = useCallback(
            (event: FocusEvent<HTMLInputElement>) => {
                if (!hideOnBlur) {
                    return;
                }
                // Check if focus is moving to a search result
                if (event.relatedTarget) {
                    const relatedTarget = event.relatedTarget as HTMLElement;
                    if (relatedTarget.matches('[data-ish-search-result-link]')) {
                        // Keep results visible when navigating to them
                        return;
                    }
                }
                // Delay to allow clicking on search results
                setTimeout(() => {
                    searchStore.setIsFocused(false);
                }, 200);
            },
            [searchStore, hideOnBlur]
        );

        return (
            <div className={classNames(Styles.bar, className)}>
                <SearchField
                    ref={searchFieldRef}
                    value={query}
                    size={size}
                    onChange={handleChange}
                    onKeyDown={handleKeydown}
                    onClear={handleClear}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    data-cy={dataCy}
                    data-pendo={dataPendo}
                />
            </div>
        );
    }
);
